/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Container, Sprite, Texture } from 'pixi.js-legacy';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { ComponentManager } from '@appCanvasCommon/component-system/ComponentManager';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { cssVar } from '@touchplan/css-var';
import { IPoolReset } from '../../../../app-data/models/data-types/GenericPool';
import { FancyDefaultStyles } from '@appCanvasCommon/text/FancyDefaultStyles';
/**
 * TimelineDisplayItem<T> is a reusable display object for rendering timeline tickets and related elements.
 */
export class TimelineDisplayItem<T> extends Container implements IPoolReset {
	/**
	 * Generic data storage for any timeline-related information.
	 */
	public data: T;
	/**
	 * Component manager for adding extensible behaviors to this display item.
	 */
	public components: ComponentManager;
	/**
	 * High-resolution text label for displaying text information.
	 * @private
	 */
	private _label: HighResolutionText;
	/**
	 * Sprite shape that provides the visual representation of the timeline item.
	 * @private
	 */
	private _shape: Sprite;
	/**
	 * Creates a new TimelineDisplayItem instance.
	 * Initializes the sprite, label, component manager, and sets up the display hierarchy.
	 */
	constructor() {
		super();
		this._shape = new Sprite();
		this._label = new HighResolutionText(``, FancyDefaultStyles.create());
		this.components = new ComponentManager(this);
		this.addChild(this._shape, this._label);
	}
	/**
	 * Gets the internal sprite object for direct changes.
	 * @returns The internal sprite instance
	 */
	get sprite(): Sprite {
		return this._shape;
	}
	/**
	 * Gets the internal label object for direct text changes.
	 * @returns The internal label instance
	 */
	get label(): HighResolutionText {
		return this._label;
	}
	/**
	 * Sets the tint color of the sprite.
	 * @param color The color to apply as tint (hex string)
	 */
	set tint(color: string) {
		this._shape.tint = color;
	}
	/**
	 * Sets the width of the display item by adjusting the sprite width.
	 * This property delegates to the internal sprite's width property.
	 * @param width The desired width in pixels
	 */
	set width(width: number) {
		this._shape.width = width;
	}
	/**
	 * Sets the height of the display item by adjusting the sprite height.
	 * This property delegates to the internal sprite's height property.
	 * @param height The desired height in pixels
	 */
	set height(height: number) {
		this._shape.height = height;
	}
	/**
	 * Gets the current height of the display item from the sprite.
	 * This property delegates to the internal sprite's height property.
	 * @returns The current height in pixels
	 */
	get height(): number {
		return this._shape.height;
	}
	/**
	 * Gets the current width of the display item from the sprite.
	 * This property delegates to the internal sprite's width property.
	 * @returns The current width in pixels
	 */
	get width(): number {
		return this._shape.width;
	}
	/**
	 * Sets the texture for the sprite.
	 * This determines the visual appearance of the timeline item (example: milestone vs constraint icon).
	 * @param texture The PIXI texture to apply to the sprite
	 */
	public setTexture(texture: Texture): void {
		this._shape.texture = texture;
	}
	/**
	 * Sets the data associated with this display item
	 * @param data The data to store in this display item
	 */
	public setData(data: T): void {
		this.data = data;
	}
	/**
	 * Configures the text label with specified text, color, and size.
	 * The label is automatically centered and positioned over the sprite.
	 * @param text The text content to display
	 * @param color The text color defaults to white
	 * @param size The font size in pixels defaults to 12px
	 */
	public setLabel(text: string, color: string = cssVar.white, size: number = 12): void {
		this._label.anchor.set(0.5, 0.5);
		this._label.position.set(this._shape.x, this._shape.y);
		this._label.text = text;
		this._label.style.fill = color;
		this._label.style.fontSize = size;
	}
	/**
	 * Resets the display item for object pool reuse.
	 * This method is called by the object pool when returning the item to the pool.
	 * Only resets the label text while preserving other properties for potential reuse.
	 * If we don't have this, it's possible the next grabbed item will have stale text data displayed
	 */
	public poolReset(): void {
		this.setLabel(``);
	}
	/**
	 * Destroys the display item and cleans up all resources.
	 * This method destroys all child objects and removes references
	 * @param options Destruction options that control how child objects are destroyed
	 */
	public destroy(options: DestroyOptions = DestroyOptions.sourceOnly): void {
		if (this._destroyed) {
			return;
		}
		super.destroy(options);
		this._label.destroy(options);
		this.components.destroy(options);

		this._label = null;
		this._shape = null;
		this.components = null;
	}
}
